import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { CustomerForm } from "@/components/customers/CustomerForm";
import ManagerPinModal from "@/components/ManagerPinModal";
import { waNumberFor } from "@/lib/wa";

// Free WhatsApp Click to Chat (Owner direction, 2026-10-25) — a customer-facing "WhatsApp
// Customer" action may ONLY ever open a chat with the number saved on the linked customer's
// account (the optional `whatsapp_number` field, else `phone`) — never a number typed in on
// the spot. Mirrors CustomerEmailGate's contract exactly: the caller has already handled the
// "no customer linked" case once before rendering this gate, so this only ever shows either
// the real "Open WhatsApp" action (via `children`, owned by the caller) or a guided "no
// WhatsApp number on file" block with an Edit Customer action that reuses the existing
// CustomerForm in place, so the caller's own dialog/work is never lost.
export default function WhatsAppCustomerGate({ customerId, number, onNumberResolved, onCustomerUpdated, children }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [business, setBusiness] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinMsg, setPinMsg] = useState("");

  const openEdit = async () => {
    try {
      const [{ data: cust }, { data: biz }] = await Promise.all([api.get(`/customers/${customerId}`), api.get("/business")]);
      setEditForm(cust);
      setBusiness(biz);
      setEditOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load the customer.");
    }
  };

  const save = async (pin = null) => {
    const body = { ...editForm };
    if (pin) body.manager_pin = pin;
    try {
      const { data } = await api.put(`/customers/${customerId}`, body);
      toast.success("Customer updated — WhatsApp number refreshed.");
      setEditOpen(false); setPinOpen(false);
      onNumberResolved?.(waNumberFor(data));
      onCustomerUpdated?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { setPinMsg(d.message); setPinOpen(true); throw new Error(d.message); }
      toast.error(apiError(d));
      throw e;
    }
  };

  if (!number) {
    return (
      <>
        <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="no-customer-whatsapp-notice">
          <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No WhatsApp number on file</p>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Add a phone or WhatsApp number in the customer's account before messaging.</p>
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-customer-for-whatsapp-btn">Edit Customer</Button>
        </div>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wa-gate-edit-customer-form">
            <DialogHeader><DialogTitle className="font-head">Edit Customer</DialogTitle></DialogHeader>
            {editForm && business && <CustomerForm form={editForm} setForm={setEditForm} business={business} mode="edit" />}
            <Button onClick={() => save()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-gate-save-customer-btn">Save &amp; Use This Number</Button>
          </DialogContent>
        </Dialog>
        <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)} title="Approval Required" reason={pinMsg} onSubmit={(pin) => save(pin)} />
      </>
    );
  }

  return children;
}
